import { Star, StarHalf } from "lucide-react";
import eddyAsset from "@/assets/testimonials/eddy-vidal-96.webp.asset.json";
import fredAsset from "@/assets/testimonials/fred-lapides-96.webp.asset.json";
import michaelAsset from "@/assets/testimonials/michael-hoffert-96.webp.asset.json";
import isaiahAsset from "@/assets/testimonials/isaiah-martinez-96.webp.asset.json";
import nationwidepowerLogo from "@/assets/logos-web/nationwidepower-240.webp.asset.json";
import uniforms2youLogo from "@/assets/logos-web/uniforms2you-300.webp.asset.json";
import hspLogo from "@/assets/logos-web/hsp-240.webp.asset.json";
import prospectdbLogo from "@/assets/logos-web/prospectdb-240.webp.asset.json";

export type Testimonial = {
  /** Lead-in sentence(s) — regular weight. */
  lead: string;
  /** The takeaway a skimmer should catch — rendered bold. */
  highlight: string;
  /** Optional closing sentence — regular weight. */
  tail?: string;
  name: string;
  title: string;
  company: string;
  photo: string;
  logo?: { src: string; alt: string; className?: string };
  /** Star rating out of 5. Defaults to 5. */
  rating?: number;
  /** Written permission to publish this quote. Unverified quotes stay hidden. */
  permissionDate?: string;
};

export const TESTIMONIALS: Testimonial[] = [
  {
    lead: "We wanted to understand why competitors were being recommended by AI instead of us.",
    highlight:
      "The visibility gaps connected directly to pipeline, closing them drove a measurable increase in qualified opportunities.",
    tail: "It went from an experiment to a revenue channel.",
    name: "Eddy Vidal",
    title: "Director of IT/Web Ops",
    company: "Nationwide Power",
    photo: eddyAsset.url,
    logo: { src: nationwidepowerLogo.url, alt: "Nationwide Power logo", className: "h-6" },
    rating: 5,
    permissionDate: "2026-10-26",
  },
  {
    lead: "Monroya showed us what traditional analytics never could:",
    highlight:
      "competitors were winning AI recommendations for our buyers' highest-intent questions.",
    tail: "Once we saw where their influence came from, we had a clear plan to close the gaps.",
    name: "Fred Lapides",
    title: "VP Marketing",
    company: "Prospect DB",
    photo: fredAsset.url,
    logo: { src: prospectdbLogo.url, alt: "Prospect DB logo", className: "h-5" },
    rating: 4.5,
    permissionDate: "2026-10-26",
  },
  {
    lead: "Our content spend used to be based on search volume and educated guesses. Monroya showed where AI was actually sending buyers, and",
    highlight: "the return beat the programs we'd been prioritizing.",
    name: "Michael Hoffert",
    title: "Co-Founder",
    company: "Uniforms 2 You",
    photo: michaelAsset.url,
    logo: { src: uniforms2youLogo.url, alt: "Uniforms 2 You logo", className: "h-5" },
    rating: 5,
    permissionDate: "2026-10-26",
  },
  {
    lead: "Monroya turned AI visibility from a marketing metric into a business conversation.",
    highlight:
      "I can show leadership which buyer questions we're winning and which competitors are taking consideration from us.",
    name: "Isaiah Martinez",
    title: "CMO",
    company: "HSP",
    photo: isaiahAsset.url,
    logo: { src: hspLogo.url, alt: "HSP logo", className: "h-6" },
    rating: 5,
    permissionDate: "2026-10-26",
  },
];

function QuoteText({ t }: { t: Testimonial }) {
  return (
    <>
      {t.lead} <strong className="font-semibold text-ink">{t.highlight}</strong>
      {t.tail ? ` ${t.tail}` : null}
    </>
  );
}

function StarRating({ rating = 5 }: { rating?: number }) {
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;
  const empty = 5 - full - (half ? 1 : 0);
  return (
    <span className="flex items-center gap-0.5 text-signal-amber" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: full }).map((_, i) => (
        <Star key={`full-${i}`} className="h-3.5 w-3.5 fill-current" aria-hidden="true" />
      ))}
      {half ? <StarHalf className="h-3.5 w-3.5 fill-current" aria-hidden="true" /> : null}
      {Array.from({ length: empty }).map((_, i) => (
        <Star key={`empty-${i}`} className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
      ))}
      <span className="sr-only">{rating}/5</span>
    </span>
  );
}

function Attribution({ t, size = 48, showRating = true }: { t: Testimonial; size?: number; showRating?: boolean }) {
  return (
    <figcaption className="mt-5 flex items-center gap-3">
      <img
        src={t.photo}
        alt={`${t.name}, ${t.title} at ${t.company}`}
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        className="shrink-0 rounded-full object-cover"
        style={{ width: size, height: size }}
      />
      <span className="min-w-0 text-sm leading-snug">
        <span className="block font-medium text-ink">{t.name}</span>
        <span className="block text-muted-foreground">
          {t.title} · {t.company}
        </span>
        {showRating ? <StarRating rating={t.rating} /> : null}
      </span>
      {t.logo ? (
        <img
          src={t.logo.src}
          alt={t.logo.alt}
          loading="lazy"
          decoding="async"
          className={`ml-auto hidden w-auto opacity-60 sm:block ${t.logo.className ?? "h-6"}`}
        />
      ) : null}
    </figcaption>
  );
}

/** Static "wall of love" grid — no rotation, nothing to skip past. */
export function TestimonialWall({
  className,
  heading = "What customers say",
  showRating = true,
}: {
  className?: string;
  heading?: string | null;
  /** Ratings are self-reported, not from a review platform; hide where that matters. */
  showRating?: boolean;
}) {
  const verified = TESTIMONIALS.filter((testimonial) => testimonial.permissionDate);
  if (verified.length === 0) return null;
  return (
    <section className={className} aria-label="Customer testimonials">
      {heading ? (
        <p className="text-center text-[13px] text-muted-foreground font-medium">
          {heading}
        </p>
      ) : null}
      <div className="mx-auto mt-6 grid w-full grid-cols-1 gap-4 text-left md:grid-cols-2">
        {verified.map((t) => (
          <figure
            key={t.name}
            className="flex h-full flex-col rounded-xl border border-border bg-panel px-6 py-6"
          >
            <blockquote className="font-serif text-base leading-relaxed text-ink/90 sm:text-lg">
              &ldquo;
              <QuoteText t={t} />
              &rdquo;
            </blockquote>
            <div className="mt-auto">
              <Attribution t={t} showRating={showRating} />
            </div>
          </figure>
        ))}
      </div>
    </section>
  );
}

/** One compact quote for placing directly beside a call to action. */
export function TestimonialSpotlight({
  index = 0,
  className,
  showRating = true,
}: {
  index?: number;
  className?: string;
  showRating?: boolean;
}) {
  const verified = TESTIMONIALS.filter((testimonial) => testimonial.permissionDate);
  if (verified.length === 0) return null;
  const t = verified[index % verified.length];
  if (!t) return null;
  return (
    <figure
      className={`mx-auto max-w-xl rounded-xl border border-border bg-panel px-5 py-5 text-left ${className ?? ""}`}
    >
      <blockquote className="font-serif text-base leading-relaxed text-ink/90">
        &ldquo;
        <QuoteText t={t} />
        &rdquo;
      </blockquote>
      <Attribution t={t} size={40} showRating={showRating} />
    </figure>
  );
}
